import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader, useBrand } from "../components/Shared";
import Money from "../components/Money";
import { fmtFCFA, fmtDate, statusBadgeClass } from "../lib/format";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Printer } from "lucide-react";

export default function Reports() {
  const [projectId, setProjectId] = useState("");
  const brand = useBrand();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const { data: detail = null } = useQuery({ queryKey: ["project", projectId], queryFn: () => api.get(`/projects/${projectId}`).then((r) => r.data), enabled: !!projectId });

  const fin = detail?.financials || {};

  return (
    <div>
      <PageHeader title="Reports & Receipts" subtitle="Generate and print professional project reports">
        <div className="flex items-center gap-2">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-64" data-testid="report-project"><SelectValue placeholder="Select a project" /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button disabled={!detail} onClick={() => window.print()} data-testid="print-report-button" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">
            <Printer className="h-4 w-4 mr-2" /> Print Report
          </Button>
        </div>
      </PageHeader>

      {!detail ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-16 text-center text-slate-400">
          Select a project above to generate its report.
        </div>
      ) : (
        <div className="print-area bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden max-w-4xl">
          <div className="bg-[#0B132B] text-white px-8 py-6 flex items-center justify-between">
            <div>
              <div className="font-heading text-2xl font-extrabold">{brand.name}</div>
              <div className="text-sm text-sky-300">Project Report · Cameroon</div>
            </div>
            {brand.logo_url ? (
              <img src={brand.logo_url} alt={brand.name} className="h-12 w-12 rounded-lg object-cover bg-white/10" />
            ) : (
              <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-sky-400 to-blue-700 flex items-center justify-center font-heading font-extrabold">ST</div>
            )}
          </div>

          <div className="p-8">
            <div className="flex items-center justify-between border-b border-slate-200 pb-4">
              <div>
                <h2 className="font-heading text-xl font-bold text-slate-900">{detail.name}</h2>
                <p className="text-sm text-slate-500">{detail.category} · {detail.location || "—"}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${statusBadgeClass(detail.status)}`}>{detail.status}</span>
            </div>

            <div className="grid grid-cols-2 gap-y-4 gap-x-8 mt-6 text-sm">
              <Cell label="Owner / Client" value={detail.owner} />
              <Cell label="Contract Value" value={fmtFCFA(detail.total_cost)} />
              <Cell label="Start Date" value={fmtDate(detail.start_date)} />
              <Cell label="Expected Completion" value={fmtDate(detail.end_date)} />
              <Cell label="Report Date" value={fmtDate(new Date().toISOString())} />
              <Cell label="Prepared From" value="STEMS System Records" />
            </div>

            {detail.description && <p className="mt-6 text-sm text-slate-600">{detail.description}</p>}

            <h3 className="font-heading font-semibold text-slate-800 mt-8 mb-3">Financial Summary</h3>
            <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
              <tbody>
                <SumRow label="Total Income" value={fmtFCFA(fin.total_income)} />
                <SumRow label="Total Expenditure" value={fmtFCFA(fin.total_expenditure)} />
                <SumRow label="Total Payments" value={fmtFCFA(fin.total_payments)} />
                <tr className="bg-slate-50 font-bold">
                  <td className="px-4 py-3 text-slate-700">Net Balance (Income − Expenditure − Payments)</td>
                  <td className="px-4 py-3 text-right"><Money value={fin.balance} showIcon={false} /></td>
                </tr>
              </tbody>
            </table>

            <div className="mt-12 flex items-end justify-between">
              <div className="text-center">
                <div className="w-48 border-t border-slate-400 pt-1 text-xs text-slate-500">Authorized Signature</div>
              </div>
              <div className="text-[10px] text-slate-400 text-right">Generated by STEMS<br />SilasTech Enterprise Management System</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const Cell = ({ label, value }) => <div><div className="text-xs text-slate-400">{label}</div><div className="font-medium text-slate-800">{value || "—"}</div></div>;
const SumRow = ({ label, value }) => <tr className="border-b border-slate-100"><td className="px-4 py-2.5 text-slate-600">{label}</td><td className="px-4 py-2.5 text-right font-mono-num font-medium text-slate-800">{value}</td></tr>;
